import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend, ReferenceLine } from "recharts";
import { money, bare } from "./ItemCard";

const GREEN = "#00A850", RED = "#E3000B", BLUE = "#0055BF", AMBER = "#D97706", INK = "#0A0A0A";

/** Final page of the internal invoice: Buy Box data + profit projections for the whole deal. */
export const ProfitPage = ({ request: r }) => {
  const a = r.analytics || {};
  const t = a.projection || {};
  const ch = a.by_channel || {};
  const items = (r.items || []).filter(i => i.projection);
  const chart = items.map(i => ({
    name: `#${bare(i.set_number)}`, cost: +(i.projection.cost * i.qty).toFixed(2),
    net: +(i.projection.net_proceeds * i.qty).toFixed(2), profit: +(i.projection.profit * i.qty).toFixed(2), channel: i.projection.channel,
  }));
  const pie = [
    { name: "Payout to seller", value: t.cost || 0, color: INK },
    { name: "Fees & shipping", value: t.fees || 0, color: AMBER },
    { name: "Projected profit", value: Math.max(0, t.profit || 0), color: GREEN },
  ].filter(d => d.value > 0);
  const chartH = Math.max(220, Math.min(720, chart.length * 26 + 40));
  return (
    <section className="space-y-5 pt-6 print-page" data-testid="request-profit-page">
      <div className="flex items-end justify-between border-b-2 border-black pb-3">
        <div>
          <div className="text-[10px] font-black tracking-[0.25em] text-gray-500">DEAL PROJECTION · {r.order_no || r.request_id}</div>
          <div className="font-head text-2xl font-black tracking-tight">Buy Box & profit outlook</div>
        </div>
        <div className="font-mono text-[10px] text-gray-500 text-right">Amazon sets → Buy Box − referral − FBA/ship/prep<br />eBay sets → PriceCharting − eBay fee (buyer pays shipping)</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Kpi label="Payout (cost)" value={money(t.cost)} testId="proj-cost" />
        <Kpi label="Projected revenue" value={money(t.revenue)} />
        <Kpi label="Fees & shipping" value={money(t.fees)} tone="warn" />
        <Kpi label="Net proceeds" value={money(t.net_proceeds)} />
        <Kpi label="Projected profit" value={money(t.profit)} tone={(t.profit || 0) >= 0 ? "good" : "bad"} testId="proj-profit" />
        <Kpi label="ROI · margin" value={t.roi_pct != null ? `${t.roi_pct}% · ${t.margin_pct}%` : "—"} tone={(t.roi_pct || 0) >= 0 ? "good" : "bad"} testId="proj-roi" />
      </div>
      {t.unprojected_units > 0 && <div className="font-mono text-[11px] text-[#D97706]" data-testid="proj-unprojected">{t.unprojected_units} unit(s) had no market price at quote time and are excluded from the projection.</div>}

      <div className="grid md:grid-cols-[1fr_300px] gap-5">
        <div className="border border-[#E4E4E0] rounded-sm p-3">
          <div className="text-[10px] font-black tracking-widest text-gray-500 mb-2">PER SET · PAYOUT VS NET PROCEEDS</div>
          <ResponsiveContainer width="100%" height={chartH}>
            <BarChart data={chart} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }} barCategoryGap={6}>
              <XAxis type="number" tickFormatter={(v) => `$${v}`} tick={{ fontSize: 10, fontFamily: "monospace" }} />
              <YAxis type="category" dataKey="name" width={62} tick={{ fontSize: 10, fontFamily: "monospace" }} />
              <Tooltip formatter={(v, k) => [money(v), { cost: "Payout", net: "Net proceeds", profit: "Profit" }[k] || k]} contentStyle={{ fontFamily: "monospace", fontSize: 11 }} />
              <ReferenceLine x={0} stroke="#999" />
              <Bar dataKey="cost" fill={INK} radius={2} name="Payout" />
              <Bar dataKey="net" radius={2} name="Net proceeds">{chart.map((d, i) => <Cell key={i} fill={d.channel === "ebay" ? AMBER : BLUE} />)}</Bar>
              <Bar dataKey="profit" radius={2} name="Profit">{chart.map((d, i) => <Cell key={i} fill={d.profit >= 0 ? GREEN : RED} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex gap-4 font-mono text-[10px] text-gray-600 mt-1">
            <Swatch c={INK} t="Payout" /><Swatch c={BLUE} t="Net · Amazon" /><Swatch c={AMBER} t="Net · eBay" /><Swatch c={GREEN} t="Profit" />
          </div>
        </div>
        <div className="space-y-4">
          <div className="border border-[#E4E4E0] rounded-sm p-3">
            <div className="text-[10px] font-black tracking-widest text-gray-500 mb-1">WHERE THE REVENUE GOES</div>
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}>{pie.map((d, i) => <Cell key={i} fill={d.color} />)}</Pie>
                <Tooltip formatter={(v) => money(v)} contentStyle={{ fontFamily: "monospace", fontSize: 11 }} />
                <Legend iconSize={8} wrapperStyle={{ fontFamily: "monospace", fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="border border-[#E4E4E0] rounded-sm p-3 text-xs space-y-2">
            <div className="text-[10px] font-black tracking-widest text-gray-500">BY CHANNEL</div>
            {["amazon", "ebay"].map(k => ch[k] && ch[k].units > 0 ? (
              <div key={k} className="flex items-center justify-between font-mono" data-testid={`proj-channel-${k}`}>
                <span className={`font-black uppercase whitespace-nowrap ${k === "ebay" ? "text-[#D97706]" : "text-[#0055BF]"}`}>{k === "ebay" ? "eBay" : "Amazon"} · {ch[k].units}u</span>
                <span className="text-right">cost {money(ch[k].cost)} → <b className={ch[k].profit >= 0 ? "text-[#00A850]" : "text-[#E3000B]"}>{money(ch[k].profit)}</b> ({ch[k].roi_pct}%)</span>
              </div>
            ) : null)}
          </div>
        </div>
      </div>

      <table className="w-full text-xs" data-testid="proj-table">
        <thead><tr className="border-b-2 border-black text-[10px] font-black tracking-widest text-gray-500">
          <th className="text-left py-1.5 px-2">Set</th><th className="text-left py-1.5 px-2">Channel</th><th className="text-right py-1.5 px-2">Buy Box</th><th className="text-right py-1.5 px-2">PC sealed</th><th className="text-right py-1.5 px-2">Sale price</th><th className="text-right py-1.5 px-2">Fees</th><th className="text-right py-1.5 px-2">Fixed</th><th className="text-right py-1.5 px-2">Net</th><th className="text-right py-1.5 px-2">Cost</th><th className="text-right py-1.5 px-2">Qty</th><th className="text-right py-1.5 px-2">Profit</th><th className="text-right py-1.5 px-2">ROI</th>
        </tr></thead>
        <tbody>
          {(r.items || []).map(i => { const p = i.pricing || {}, pj = i.projection; return (
            <tr key={i.set_number} className="border-b border-[#E4E4E0] font-mono" data-testid={`proj-row-${i.set_number}`}>
              <td className="py-1.5 px-2 font-bold">#{bare(i.set_number)} <span className="font-sans font-normal text-gray-600">{i.name}</span></td>
              <td className={`py-1.5 px-2 font-black uppercase text-[10px] ${pj?.channel === "ebay" ? "text-[#D97706]" : "text-[#0055BF]"}`}>{pj?.channel || "—"}</td>
              <td className="py-1.5 px-2 text-right">{p.amazon_buy_box != null ? money(p.amazon_buy_box) : <span className="text-gray-400">none</span>}</td>
              <td className="py-1.5 px-2 text-right">{money(p.basis)}</td>
              <td className="py-1.5 px-2 text-right">{pj ? money(pj.sale_price) : "—"}</td>
              <td className="py-1.5 px-2 text-right text-[#D97706]">{pj ? money(pj.fees) : "—"}</td>
              <td className="py-1.5 px-2 text-right text-[#D97706]">{pj ? money(pj.fixed_costs) : "—"}</td>
              <td className="py-1.5 px-2 text-right">{pj ? money(pj.net_proceeds) : "—"}</td>
              <td className="py-1.5 px-2 text-right">{money(i.offer)}</td>
              <td className="py-1.5 px-2 text-right">{i.qty}</td>
              <td className={`py-1.5 px-2 text-right font-bold ${pj ? (pj.profit >= 0 ? "text-[#00A850]" : "text-[#E3000B]") : ""}`}>{pj ? money(pj.profit * i.qty) : "—"}</td>
              <td className="py-1.5 px-2 text-right">{pj?.roi_pct != null ? `${pj.roi_pct}%` : "—"}</td>
            </tr>
          ); })}
        </tbody>
      </table>
    </section>
  );
};

function Kpi({ label, value, tone, testId }) {
  const c = tone === "good" ? "text-[#00A850]" : tone === "bad" ? "text-[#E3000B]" : tone === "warn" ? "text-[#D97706]" : "text-black";
  return (
    <div className="border border-[#E4E4E0] rounded-sm p-3 bg-[#F6F6F3]">
      <div className="text-[9px] font-black tracking-widest text-gray-500 uppercase">{label}</div>
      <div className={`font-head text-xl font-black ${c}`} data-testid={testId}>{value}</div>
    </div>
  );
}
const Swatch = ({ c, t }) => <span className="inline-flex items-center gap-1"><i className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: c }} />{t}</span>;
